import type { Plugin, ResolvedConfig } from "vite";
import type { HotshotConfig, WSMessage } from "@hotshot-it/shared";
import { HotshotWebSocketServer, AgentHandler } from "@hotshot-it/core";
import type { WebSocket } from "ws";

const DEFAULT_PORT = 3737;
const DEFAULT_MODEL = "claude-sonnet-4-5";

export default function hotshot(config?: Partial<HotshotConfig>): Plugin {
  const pluginConfig = config ?? {};
  let resolvedConfig: ResolvedConfig;
  let wsServer: HotshotWebSocketServer | null = null;
  let agentHandler: AgentHandler | null = null;

  return {
    name: "@hotshot-it/vite-plugin",

    configResolved(config) {
      resolvedConfig = config;
    },

    configureServer(server) {
      // Only run in development mode
      if (resolvedConfig.command !== "serve") {
        return;
      }

      const port = pluginConfig.port ?? DEFAULT_PORT;
      const model = pluginConfig.model ?? DEFAULT_MODEL;

      console.log(`[Hotshot] Starting WebSocket server on port ${port}`);
      console.log(`[Hotshot] Using Claude CLI with model: ${model}`);

      // Initialize WebSocket server
      wsServer = new HotshotWebSocketServer({
        port,
        server: server.httpServer!,
      });

      // Initialize Agent handler
      agentHandler = new AgentHandler({
        cwd: resolvedConfig.root,
        model,
        systemPromptAdditions: pluginConfig.systemPromptAdditions,
        permissionMode: pluginConfig.permissionMode,
        broadcast: (message) => wsServer?.broadcast(message),
      });

      // Set up message handler
      wsServer.onMessage = async (ws: WebSocket, message: WSMessage) => {
        try {
          await handleMessage(ws, message, agentHandler!, wsServer!);
        } catch (error) {
          console.error("[Hotshot] Message handler error:", error);
          wsServer!.sendError(
            ws,
            "Failed to process message",
            error instanceof Error ? error.message : error,
          );
        }
      };

      console.log("[Hotshot] Plugin initialized successfully");
      console.log("[Hotshot] Waiting for browser extension to connect...");
    },

    buildEnd() {
      // Clean up on server shutdown
      if (wsServer) {
        console.log("[Hotshot] Shutting down WebSocket server");
        wsServer.close();
      }
    },
  };
}

/**
 * Handle incoming WebSocket messages
 */
async function handleMessage(
  ws: WebSocket,
  message: WSMessage,
  agentHandler: AgentHandler,
  wsServer: HotshotWebSocketServer,
): Promise<void> {
  if (message.type === "component:selected") {
    console.log("[Hotshot] Component selected:", message.component.name);

    // Check if session exists for this component and send info to frontend
    const sessionInfo = agentHandler.getSessionInfo(message.component);
    if (sessionInfo) {
      const componentId = `${message.component.file}:${message.component.name}`;
      console.log("[Hotshot] Existing session found for component:", componentId);
      ws.send(JSON.stringify({
        type: 'session:info',
        sessionId: sessionInfo.sessionId,
        componentId,
        messageCount: sessionInfo.messageCount,
        isNew: false,
      }));
    }
  } else if (message.type === "chat:message") {
    if (message.mode === 'general') {
      console.log(
        `[Hotshot] General chat message: "${message.message}"`,
        message.files ? `with ${message.files.length} files` : '',
      );
      await agentHandler.handleGeneralChat(
        message.message,
        message.files,
      );
    } else {
      console.log(
        `[Hotshot] Chat message for ${message.component?.name}: "${message.message}"`,
        message.files ? `with ${message.files.length} files` : '',
      );
      if (message.component) {
        await agentHandler.handleChat(
          message.component,
          message.message,
          message.scope,
          message.files,
        );
      }
    }
  } else if (message.type === "session:reset") {
    console.log("[Hotshot] Resetting session for:", message.component.name);
    agentHandler.resetSession(message.component);
    // Acknowledge reset
    ws.send(JSON.stringify({
      type: 'session:info',
      sessionId: '',
      componentId: `${message.component.file}:${message.component.name}`,
      messageCount: 0,
      isNew: true,
    }));
  } else if (message.type === "session:clear-all") {
    console.log("[Hotshot] Clearing all sessions");
    agentHandler.clearAllSessions();
    // Send acknowledgment back to frontend
    ws.send(JSON.stringify({
      type: 'session:info',
      sessionId: '',
      componentId: '',
      messageCount: 0,
      isNew: true,
    }));
  } else if (message.type === "visual:apply") {
    console.log(
      `[Hotshot] Applying ${message.edits.length} visual edits for ${message.component.name}`,
    );
    await agentHandler.handleVisualEdits(
      message.component,
      message.edits,
    );
  } else if (message.type === "error" || message.type === "connection:ready") {
    // Internal messages, ignore
  } else {
    wsServer.sendError(ws, `Unknown message type: ${(message as any).type}`);
  }
}
